import React, { useState, useEffect, useContext } from 'react';
import { ApiContext } from '../../App';
import { Plus, Check, Trash2, Calendar, Clock, GripVertical, Pencil, X, Save } from 'lucide-react';
import { toast } from 'react-toastify';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Composant pour une tâche draggable
function SortableTaskItem({ task, onToggleDone, onDeleteTask, onEditTask, formatDate, isOverdue, editingTaskId, onStartEdit, onCancelEdit }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task._id });

  const [editText, setEditText] = useState(task.text);
  const [editDate, setEditDate] = useState(task.reminderDate ? new Date(task.reminderDate).toISOString().split('T')[0] : '');

  const isEditing = editingTaskId === task._id;

  // Mettre à jour les états locaux quand la tâche change
  useEffect(() => {
    setEditText(task.text);
    setEditDate(task.reminderDate ? new Date(task.reminderDate).toISOString().split('T')[0] : '');
  }, [task.text, task.reminderDate]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleSave = () => {
    onEditTask(task._id, {
      text: editText,
      reminderDate: editDate || null,
    });
  };

  const handleCancel = () => {
    setEditText(task.text);
    setEditDate(task.reminderDate ? new Date(task.reminderDate).toISOString().split('T')[0] : '');
    onCancelEdit();
  };

  if (isEditing) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className="flex flex-col gap-3 p-4 rounded-lg border border-indigo-300 bg-indigo-50 transition-all"
      >
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description de la tâche *
            </label>
            <input
              type="text"
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
              <Calendar size={14} className="mr-1" />
              Date de rappel (optionnelle)
            </label>
            <input
              type="date"
              value={editDate}
              onChange={(e) => setEditDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm"
            >
              <Save size={14} />
              Enregistrer
            </button>
            <button
              onClick={handleCancel}
              className="flex items-center gap-1 px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
            >
              <X size={14} />
              Annuler
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-start gap-3 p-4 rounded-lg border transition-all hover:shadow-md ${
        task.done
          ? 'bg-gray-50 border-gray-200'
          : 'bg-white border-gray-300'
      }`}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing flex-shrink-0 mt-0.5"
      >
        <GripVertical className="h-5 w-5 text-gray-400" />
      </div>

      <button
        onClick={() => onToggleDone(task._id, task.done)}
        className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
          task.done
            ? 'bg-green-500 border-green-500'
            : 'border-gray-300 hover:border-indigo-500'
        }`}
      >
        {task.done && <Check size={14} className="text-white" />}
      </button>

      <div className="flex-1 min-w-0">
        <p className={`text-sm ${task.done ? 'line-through text-gray-500' : 'text-gray-800'}`}>
          {task.text}
        </p>
        {task.reminderDate && (
          <div className="flex items-center gap-2 mt-1">
            <div className={`flex items-center gap-1 text-xs ${
              isOverdue(task.reminderDate) && !task.done
                ? 'text-red-600 font-medium'
                : 'text-gray-500'
            }`}>
              {isOverdue(task.reminderDate) && !task.done ? (
                <>
                  <Clock size={12} />
                  <span>En retard - {formatDate(task.reminderDate)}</span>
                </>
              ) : (
                <>
                  <Calendar size={12} />
                  <span>{formatDate(task.reminderDate)}</span>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <button
        onClick={() => onStartEdit(task._id)}
        className="flex-shrink-0 p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
        title="Modifier la tâche"
      >
        <Pencil size={16} />
      </button>

      <button
        onClick={() => onDeleteTask(task._id)}
        className="flex-shrink-0 p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
        title="Supprimer la tâche"
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
}

const PositionTasks = ({ positionId }) => {
  const API = useContext(ApiContext);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTaskText, setNewTaskText] = useState('');
  const [newTaskDate, setNewTaskDate] = useState('');
  const [showAddTask, setShowAddTask] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionId]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await API.get('/api/reminders', {
        params: { type: 'position', positionId },
      });
      if (response.data.success) {
        setTasks(response.data.reminders);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Erreur lors du chargement des tâches');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();

    if (!newTaskText.trim()) {
      toast.error('Veuillez saisir une description de la tâche');
      return;
    }

    try {
      const taskData = {
        text: newTaskText,
        positionId,
      };

      // Ajouter la date seulement si elle est définie
      if (newTaskDate) {
        taskData.reminderDate = newTaskDate;
      }

      const response = await API.post('/api/position-tasks', taskData);

      if (response.data.success) {
        setTasks([response.data.task, ...tasks]);
        setNewTaskText('');
        setNewTaskDate('');
        setShowAddTask(false);
        toast.success('Tâche créée avec succès');
      }
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la création de la tâche');
    }
  };

  const handleToggleDone = async (taskId, currentDone) => {
    try {
      const response = await API.put(`/api/reminders/${taskId}`, {
        done: !currentDone,
      });

      if (response.data.success) {
        setTasks(tasks.map(task =>
          task._id === taskId ? { ...task, done: !currentDone } : task
        ));
      }
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Erreur lors de la mise à jour de la tâche');
    }
  };

  const handleDeleteTask = async (taskId) => {

    try {
      const response = await API.delete(`/api/reminders/${taskId}`);

      if (response.data.success) {
        setTasks(tasks.filter(task => task._id !== taskId));
        toast.success('Tâche supprimée');
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Erreur lors de la suppression de la tâche');
    }
  };

  const handleStartEdit = (taskId) => {
    setEditingTaskId(taskId);
  };

  const handleCancelEdit = () => {
    setEditingTaskId(null);
  };

  const handleEditTask = async (taskId, updates) => {
    if (!updates.text || !updates.text.trim()) {
      toast.error('La description de la tâche ne peut pas être vide');
      return;
    }

    try {
      const response = await API.put(`/api/reminders/${taskId}`, updates);

      if (response.data.success) {
        setTasks(tasks.map(task =>
          task._id === taskId ? { ...task, ...updates } : task
        ));
        setEditingTaskId(null);
        toast.success('Tâche modifiée avec succès');
      }
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Erreur lors de la modification de la tâche');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const isOverdue = (reminderDate) => {
    if (!reminderDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = new Date(reminderDate);
    dueDate.setHours(0, 0, 0, 0);
    return dueDate < today;
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setTasks(prev => {
      const oldIndex = prev.findIndex(task => task._id === active.id);
      const newIndex = prev.findIndex(task => task._id === over.id);

      const newTasks = arrayMove(prev, oldIndex, newIndex);

      // Sauvegarder le nouvel ordre dans le backend
      updateTasksOrder(newTasks);

      return newTasks;
    });
  };

  const updateTasksOrder = async (orderedTasks) => {
    try {
      const taskIds = orderedTasks.map(task => task._id);
      await API.put('/api/position-tasks/reorder', {
        positionId,
        taskIds
      });
    } catch (error) {
      console.error('Error updating tasks order:', error);
      toast.error('Erreur lors de la mise à jour de l\'ordre des tâches');
    }
  };



  return (
    <div className="bg-white rounded-lg border p-6 mt-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-800 flex items-center">
          <Check className="mr-2" size={20} />
          Tâches à effectuer
        </h2>
        <button
          onClick={() => setShowAddTask(!showAddTask)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm"
        >
          <Plus size={16} />
          Nouvelle tâche
        </button>
      </div>

      {showAddTask && (
        <form onSubmit={handleCreateTask} className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description de la tâche *
              </label>
              <input
                type="text"
                value={newTaskText}
                onChange={(e) => setNewTaskText(e.target.value)}
                placeholder="Ex: Relancer le candidat, préparer l'entretien..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                <Calendar size={14} className="mr-1" />
                Date de rappel (optionnelle)
              </label>
              <input
                type="date"
                value={newTaskDate}
                onChange={(e) => setNewTaskDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm"
              >
                Créer la tâche
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddTask(false);
                  setNewTaskText('');
                  setNewTaskDate('');
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
              >
                Annuler
              </button>
            </div>
          </div>
        </form>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={tasks.map(task => task._id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {tasks.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Check size={48} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm">Aucune tâche pour le moment</p>
                <p className="text-xs mt-1">Cliquez sur "Nouvelle tâche" pour commencer</p>
              </div>
            ) : (
              tasks.map((task) => (
                <SortableTaskItem
                  key={task._id}
                  task={task}
                  onToggleDone={handleToggleDone}
                  onDeleteTask={handleDeleteTask}
                  onEditTask={handleEditTask}
                  formatDate={formatDate}
                  isOverdue={isOverdue}
                  editingTaskId={editingTaskId}
                  onStartEdit={handleStartEdit}
                  onCancelEdit={handleCancelEdit}
                />
              ))
            )}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default PositionTasks;
